import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { UnidataTable } from '~/components';
import {
  CUSTOMIZACAO_RAIZEN,
  DESCARGA_NF,
  MODULO_20_GRAUS,
  SOBRAS_E_FALTAS,
  MODULO_FERROVIA,
} from '~/constants/modules';
import { t, can, number } from '~/helpers';
import { handleExportGeneric } from '~/helpers/exportReports';
import {
  deleteBico,
  findBico,
  recordFiltering,
} from '~/store/modules/bico/actions';

import Form from './form';

function BicoList() {
  const redux = useSelector((state) => state.bico);

  const status = useMemo(
    () => [
      { label: 'L', value: t('s.geral.opcoes.liberado') },
      { label: 'B', value: t('s.geral.opcoes.bloqueado') },
      { label: 'C', value: t('s.geral.opcoes.cancelado') },
    ],
    []
  );

  const header = useMemo(() => {
    const items = [
      {
        id: 'idbico',
        label: t('s.configuracao.bico.idbico'),
        filter: { type: 'text' },
      },
      {
        id: 'codigonomeposto',
        label: t('s.geral.postocomboio'),
        filter: { type: 'text' },
      },
      {
        id: 'idterminal',
        label: t('s.configuracao.bico.idterminal'),
        filter: { type: 'text' },
      },
      {
        id: 'nomeabreviadoproduto',
        label: t('s.configuracao.bico.nomeabreviadoproduto'),
        filter: { type: 'text' },
      },
      {
        id: 'fatorkbico',
        label: t('s.geral.fatork'),
        align: 'right',
        formatted: (value) => number(value, 3),
      },
      {
        id: 'statusbico',
        label: t('s.configuracao.bico.statusbico'),
        formatted: (value) => status.find((s) => s.label === value).value || '',
        filter: { type: 'select', options: status },
      },
    ];

    if (can(DESCARGA_NF) || can(SOBRAS_E_FALTAS))
      items.splice(3, 0, {
        id: 'idtanquevirtual',
        label: t('s.configuracao.bico.idtanquevirtual'),
        filter: { type: 'text' },
      });

    return items;
  }, [status]);

  const fieldlist = [
    'idbico',
    'codigonomeposto',
    'idterminal',
    'codigoauxiliarbico',
  ];

  if (can(CUSTOMIZACAO_RAIZEN)) {
    fieldlist.push('codigobicocosanbico');
    fieldlist.push('codigobombacosanbico');
  }

  if (can(SOBRAS_E_FALTAS) || can(DESCARGA_NF)) {
    fieldlist.push('idtanquevirtual');
  } else {
    fieldlist.push('idproduto');
  }

  fieldlist.push('fatorkbico');

  if (can(MODULO_20_GRAUS)) {
    fieldlist.push('tipotemperaturabico');
  }

  fieldlist.push(
    'descricaomedidor',
    'timeoutbico',
    'timeoutvazamentobico',
    'volumeminimovazbico',
    'automaticobico',
    'tempoligabombabico',
    'afericaoautomaticabico',
    'digitosbico',
    'tipopredeterminacaobico',
    'digitaencerrantebico',
    'multencerhrsbico',
    'tipopulserbico',
    'monitorainclinacaodbicbico',
    'habilitatrocatanque',
    'tmpdesligabombabico',
    'percsubirvazaobico',
    'volsubirvazaobico',
    'voldescervazaobico',
    'vazaominimavazbico',
    'stoplcrbico',
    'tipooverfillbico',
    'canalcbcbico',
    'enderecocbcbico',
    'numeroladocbcbico',
    'casasvalortotalcbcbico',
    'casasvalorlitrocbcbico',
    'casasvalorunitariocbcbico',
    'exibevolumebico',
    'numerobombasbico'
  );

  if (can(MODULO_FERROVIA)) {
    fieldlist.push(
      'sinalhomemmortobico',
      'sinalvalvulaabertabico',
      'vazaomaximabico'
    );
  }

  fieldlist.push('statusbico');

  const substituirHeader = {
    codigonomeposto: t('s.geral.postocomboio'),
    idbico: t('s.configuracao.bico.idbico'),
    idmedidor: t('s.configuracao.bico.idmedidor'),
    codigoauxiliarbico: t('s.configuracao.bico.codigoauxiliarbico'),
    idproduto: t('s.configuracao.bico.idproduto'),
    idterminal: t('s.configuracao.bico.idterminal'),
    timeoutbico: t('s.configuracao.bico.timeoutbico'),
    timeoutvazamentobico: t('s.configuracao.bico.timeoutvazamentobico'),
    volumeminimovazbico: t('s.configuracao.bico.volumeminimovazbico'),
    automaticobico: t('s.configuracao.bico.automaticobico'),
    tempoligabombabico: t('s.configuracao.bico.tempoligabombabico'),
    afericaoautomaticabico: t('s.geral.afericaoautomatica'),
    digitosbico: t('s.configuracao.bico.digitosbico'),
    tipopredeterminacaobico: t('s.configuracao.bico.tipopredeterminacaobico'),
    digitaencerrantebico: t('s.configuracao.bico.digitaencerrantebico'),
    multencerhrsbico: t('s.configuracao.bico.multencerhrsbico'),
    tipopulserbico: t('s.configuracao.bico.tipopulserbico'),
    monitorainclinacaodbicbico: t(
      's.configuracao.bico.monitorainclinacaodbicbico'
    ),
    habilitatrocatanque: t('s.configuracao.bico.habilitatrocatanque'),
    idtanquevirtual: t('s.configuracao.bico.idtanquevirtual'),
    tmpdesligabombabico: t('s.configuracao.bico.tmpdesligabombabico'),
    percsubirvazaobico: t('s.configuracao.bico.percsubirvazaobico'),
    volsubirvazaobico: t('s.configuracao.bico.volsubirvazaobico'),
    voldescervazaobico: t('s.configuracao.bico.voldescervazaobico'),
    vazaominimavazbico: t('s.configuracao.bico.vazaominimavazbico'),
    statusbico: t('s.configuracao.bico.statusbico'),
    stoplcrbico: t('s.configuracao.bico.stoplcrbico'),
    tipooverfillbico: t('s.configuracao.bico.tipooverfillbico'),
    canalcbcbico: t('s.configuracao.bico.canalcbcbico'),
    enderecocbcbico: t('s.configuracao.bico.enderecocbcbico'),
    numeroladocbcbico: t('s.configuracao.bico.numeroladocbcbico'),
    casasvalortotalcbcbico: t('s.configuracao.bico.casasvalortotalcbcbico'),
    casasvalorlitrocbcbico: t('s.configuracao.bico.casasvalorlitrocbcbico'),
    casasvalorunitariocbcbico: t(
      's.configuracao.bico.casasvalorunitariocbcbico'
    ),
    exibevolumebico: t('s.configuracao.bico.exibevolumebico'),
    numerobombasbico: t('s.configuracao.bico.numerobombasbico'),
    vazaomaximabico: t('s.configuracao.bico.vazaomaximabico'),
    fatorkbico: t('s.geral.fatork'),
    codigobicocosanbico: t('s.configuracao.bico.codigobicocosanbico'), // customizacao raizen
    codigobombacosanbico: t('s.configuracao.bico.codigobombacosanbico'), // customizacao raizen
    tipotemperaturabico: t('s.configuracao.bico.tipotemperaturabico'),
    sinalhomemmortobico: t('s.geral.sinalhomemmorto'),
    sinalvalvulaabertabico: t('s.geral.sinalvalvulaaberta'),
    vazaomaximabico: t('s.operacao.abastecimento.vazaomaximamovimentacao'),
    nomeabreviadoproduto: t('s.configuracao.bico.nomeabreviadoproduto'),
  };

  return (
    <UnidataTable
      fixed
      redux={redux}
      header={header}
      read={recordFiltering}
      find={findBico}
      remove={deleteBico}
      form={Form}
      keys={['idbico', 'idposto']}
      detach="idbico"
      pdf
      excel
      onExport={(formato) =>
        handleExportGeneric({
          endpoint: 'bicos',
          formato,
          fields: fieldlist,
          headers: substituirHeader,
          redux,
          ordination: redux.params.ordination,
          orderfield: redux.params.orderfield,
          title: 'Relatorio de Bicos',
          compactQuery: true,
          substituirValores: {
            statusbico: {
              L: t('s.geral.opcoes.liberado'),
              B: t('s.geral.opcoes.bloqueado'),
              C: t('s.geral.opcoes.cancelado'),
            },
            automaticobico: {
              '0': t('s.geral.opcoes.bicoautomaticopulser'),
              '1': t('s.geral.opcoes.bicoautomaticoconcentrador'),
              '2': t('s.geral.opcoes.bicoautomaticobombaeletronica'),
            },
            digitaencerrantebico: {
              '0': t('s.geral.opcoes.nao'),
              '1': t('s.geral.opcoes.sim'),
            },
            monitorainclinacaodbicbico: {
              '0': t('s.geral.nivelseguranca.semseguranca'),
              '1': t('s.geral.nivelseguranca.comseguranca'),
            },
            habilitatrocatanque: {
              N: t('s.geral.opcoes.nao'),
              S: t('s.geral.opcoes.sim'),
              '0': t('s.geral.opcoes.nao'),
              '1': t('s.geral.opcoes.sim'),
            },
            stoplcrbico: {
              '0': t('s.geral.opcoes.nao'),
              '1': t('s.geral.opcoes.sim'),
            },
            exibevolumebico: {
              N: t('s.geral.opcoes.nao'),
              S: t('s.geral.opcoes.sim'),
            },
            tipotemperaturabico: {
              '0': t('s.configuracao.bico.tipotemperaturabico.semtemperatura'),
              '1': t('s.configuracao.bico.tipotemperaturabico.manualdigitada'),
              '2': t('s.configuracao.bico.tipotemperaturabico.pt100'),
              '3': t('s.configuracao.bico.tipotemperaturabico.tanque'),
            },
            sinalhomemmortobico: {
              '0': t('s.geral.opcoes.nao'),
              '1': t('s.geral.opcoes.sim'),
            },
            sinalvalvulaabertabico: {
              '0': t('s.geral.opcoes.nao'),
              '1': t('s.geral.opcoes.sim'),
            },
            afericaoautomaticabico: {
              N: t('s.geral.opcoes.nao'),
              S: t('s.geral.opcoes.sim'),
            },
            tipopredeterminacaobico: {
              '0': 'Valor',
              '1': 'Volume',
              '2': 'Não Predetermina',
            },
          },
        })
      }
    />
  );
}

export default BicoList;
